import {
  getSubscriptionsUsageResolved,
  InnovyzeRole,
  setDefaultSite,
  setFeatures,
  setIsInitializing,
  setIsLoading,
  setSettings,
  setToken,
  setUserContext,
} from '@innovyze/stylovyze';

import {
  getEmbeddedProductLineName,
  getEmbeddedTenantId,
  isEmbedHostContextReady,
  setEmbeddedAuthToken,
  setEmbeddedProductLineName,
  setEmbeddedTenantId,
} from '@/services/agentBaseUrl';
import { store } from '@/store';

/** postMessage type from a host app (e.g. Info360 Model) updating embed context. */
export const BERNOULLI_EMBED_CONTEXT_MESSAGE = 'bernoulli-embed-context';

/** Fired when embed tenant/product changes so chat can reset stale tool results. */
export const BERNOULLI_EMBED_TENANT_CHANGED_EVENT =
  'bernoulli-embed-tenant-changed';

/** Fired after host context (token/tenant/product) is applied in memory. */
export const BERNOULLI_EMBED_CONTEXT_APPLIED_EVENT =
  'bernoulli-embed-context-applied';

let allowedParentOrigin: string | undefined;

/** Parse and normalize a host origin (http/https only). */
function normalizeParentOrigin(value: string): string | undefined {
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return undefined;
    }
    return url.origin;
  } catch {
    return undefined;
  }
}

/**
 * Hydrate minimal Stylovyze state so ApplicationWrapper can render in iframe embeds.
 * Host postMessage does not carry a role; READ_ONLY is the least-privileged
 * InnovyzeRole that satisfies ApplicationWrapper's user-context gate.
 */
function syncEmbedStylovyzeShell(tenantId: string): void {
  store.dispatch(
    setUserContext({
      defaultSite: tenantId,
      defaultSiteProjectId: tenantId,
      defaultTeamId: tenantId,
      teamId: tenantId,
      innovyzeRole: InnovyzeRole.READ_ONLY,
      id: 'embed-host',
      // FeatureFlagProvider waits for a truthy email before LaunchDarkly init.
      email: 'embed-host@local',
      name: 'Embed Host',
      entitlements: [],
      productAccessLevels: [],
      termsAndConditions: { status: 'resolved' },
    }),
  );
  // ApplicationWrapper LoadingProvider keeps the blue spinner while
  // authenticationContext.isInitializing is true; embed mode skips Forge auth.
  store.dispatch(setIsInitializing(false));
  // SettingsProvider reads authenticationContext.defaultSite, not user.defaultSite.
  store.dispatch(setDefaultSite(tenantId));
  // isTenantAware=false skips ExpiredTenantDialogWrapper subscription init, so
  // subscriptionsUsage.initialized never flips true without an explicit resolve.
  store.dispatch(getSubscriptionsUsageResolved({ subscriptions: [] }));
  // Bypass LaunchDarkly and company-settings API gates for host-provided auth.
  store.dispatch(setFeatures({}));
  store.dispatch(
    setSettings({
      // Reducer merges with default company settings; org id is enough for embed.
      companySettings: { organization: tenantId } as Parameters<
        typeof setSettings
      >[0]['companySettings'],
    }),
  );
  store.dispatch(setIsLoading(false));
}

/** Tenant + product from URL/postMessage before authToken arrives in iframe embeds. */
function isEmbedShellPartiallyReady(): boolean {
  return Boolean(getEmbeddedTenantId() && getEmbeddedProductLineName());
}

function syncEmbedStylovyzeFromHostContext(): void {
  const tenantId = getEmbeddedTenantId();
  if (!tenantId) {
    return;
  }

  if (isEmbedHostContextReady()) {
    syncEmbedStylovyzeShell(tenantId);
    return;
  }

  // URL bootstrap and early postMessage often have tenant/product before token.
  if (window.self !== window.top && isEmbedShellPartiallyReady()) {
    syncEmbedStylovyzeShell(tenantId);
  }
}

export type EmbedHostContextMessage = {
  type?: string;
  authToken?: string;
  tenantId?: string;
  productLineName?: string;
};

/**
 * Host-provided auth/tenant/product for agent calls. Kept in memory only — not
 * sessionStorage — so stylovyze ApplicationWrapper keeps using Asset-accessible sites.
 */
export function applyEmbedHostContext(
  tenantId?: string,
  productLineName?: string,
  authToken?: string,
): void {
  const trimmedToken = authToken?.trim();
  const trimmedTenant = tenantId?.trim();
  const trimmedProduct = productLineName?.trim();
  const previousTenant = getEmbeddedTenantId();
  const previousProduct = getEmbeddedProductLineName();

  if (trimmedToken) {
    setEmbeddedAuthToken(trimmedToken);
    // AlternateAuthenticationWrapper picks its auth provider from
    // authenticationContext.token / the Forge+Auth0 cookies. Cross-site iframes
    // never see those cookies (SameSite), and prepareEmbedIframeAuthBypass erases
    // the leftovers, so without this dispatch the gate resolves no provider and
    // renders an empty fragment — #inno-app never mounts and #root stays blank.
    store.dispatch(setToken(trimmedToken));
  }

  if (trimmedTenant) {
    setEmbeddedTenantId(trimmedTenant);
    sessionStorage.removeItem('tenantId');
  }

  if (trimmedProduct) {
    setEmbeddedProductLineName(trimmedProduct);
    sessionStorage.removeItem('productLineName');
  }

  const tenantChanged = Boolean(
    trimmedTenant && (!previousTenant || trimmedTenant !== previousTenant),
  );
  const productChanged = Boolean(
    trimmedProduct && (!previousProduct || trimmedProduct !== previousProduct),
  );

  if (tenantChanged || productChanged) {
    window.dispatchEvent(
      new CustomEvent(BERNOULLI_EMBED_TENANT_CHANGED_EVENT, {
        detail: {
          tenantId: trimmedTenant ?? previousTenant,
          productLineName: trimmedProduct ?? previousProduct,
        },
      }),
    );
  }

  if (trimmedToken || trimmedTenant || trimmedProduct) {
    window.dispatchEvent(
      new CustomEvent(BERNOULLI_EMBED_CONTEXT_APPLIED_EVENT),
    );
    syncEmbedStylovyzeFromHostContext();
  }
}

/** Apply host context from /embed URL query params before React mounts. */
export function bootstrapEmbedHostContextFromUrl(): void {
  if (!window.location.pathname.startsWith('/embed')) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const parentOrigin = params.get('parentOrigin')?.trim();
  if (parentOrigin) {
    allowedParentOrigin = normalizeParentOrigin(parentOrigin);
  } else if (document.referrer) {
    allowedParentOrigin = normalizeParentOrigin(document.referrer);
  }

  applyEmbedHostContext(
    params.get('tenantId') ?? undefined,
    params.get('productLineName') ?? undefined,
  );
}

/**
 * Listen for host org-switcher updates from Model without a full iframe reload.
 * Call only on the /embed route (see index.tsx).
 *
 * Origin is validated on every message before bearer tokens are accepted
 * (privacy/bearer — javascript_lang_message_handler_origin).
 */
export function subscribeEmbedHostContextFromPostMessage(): () => void {
  const handler = (event: MessageEvent) => {
    if (event.source !== window.parent) {
      return;
    }
    if (!allowedParentOrigin || event.origin !== allowedParentOrigin) {
      return;
    }

    const data = event.data as EmbedHostContextMessage;
    if (data?.type !== BERNOULLI_EMBED_CONTEXT_MESSAGE) {
      return;
    }

    applyEmbedHostContext(data.tenantId, data.productLineName, data.authToken);
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
